import { Component, Input, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { CoreService } from '../../services'
import { MenuItems, MenuService } from '../../configs/menu.service'
import { TranslateService } from '@ngx-translate/core'
import { tap } from 'rxjs/operators'
import { Subscription } from 'rxjs'
import { Emittable, Emitter } from '@ngxs-labs/emitter'
import { PlugplayState } from '../../pages/plug-play/states/plugplay-state.service'

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnDestroy {
  @Input() isShown = false
  @Input() isWizardShown = false
  @Emitter(PlugplayState.setWizardVisibility)
  public readonly setWizardVisibility: Emittable<boolean>
  public lastOpenedSubTree: string
  public menuItems: MenuItems[] = []
  public isMenuReady = false
  private subscriptions = new Subscription()
  private treeviewInitTimer: ReturnType<typeof setTimeout> | undefined
  private destroyed = false
  constructor(
    private router: Router,
    private core: CoreService,
    private menuService: MenuService,
    private translateService: TranslateService,
  ) {
    this.lastOpenedSubTree = this.core.services.storage.getSync(
      this.core.services.storage.keys.routing.lastOpenedSubTree,
    )
    this.core.services.user.currentUserEmitter.subscribe().then(user => {
      if (this.destroyed) {
        return
      }
      if (user && user.id > 0) {
        this.subscriptions.add(
          this.translateService.onLangChange
            .pipe(
              tap(x => {
                this.menuItems = this.menuService.menuItems(this.core.services.user)
              }),
            )
            .subscribe(),
        )
        this.menuItems = this.menuService.menuItems(this.core.services.user)
        setTimeout(() => {
          if (this.destroyed || this.isMenuReady) {
            return
          }
          this.isMenuReady = true
          this.isShown = true
          this.scheduleTreeviewInit()
        }, 300)
      } else {
        this.menuItems = []
        this.clearTreeviewInit()
        setTimeout(() => {
          if (this.destroyed) {
            return
          }
          this.isMenuReady = false
          this.isShown = false
        }, 300)
      }
    })
  }

  ngOnDestroy(): void {
    this.destroyed = true
    this.clearTreeviewInit()
    this.subscriptions.unsubscribe()
  }

  // scripts.js (jQuery + AdminLTE) is deferred and can run after the app boots.
  // A bare `$` then throws ReferenceError. Retry until both the global and the menu exist.
  private scheduleTreeviewInit(attempt = 0): void {
    this.clearTreeviewInit()
    this.treeviewInitTimer = setTimeout(() => this.initTreeview(attempt), 300)
  }

  private clearTreeviewInit(): void {
    if (this.treeviewInitTimer !== undefined) {
      clearTimeout(this.treeviewInitTimer)
      this.treeviewInitTimer = undefined
    }
  }

  private initTreeview(attempt = 0): void {
    this.treeviewInitTimer = undefined
    if (!this.isShown || !this.isMenuReady) {
      return
    }
    try {
      const jq = typeof window !== 'undefined' ? (window as any).jQuery || (window as any).$ : null
      if (typeof jq === 'function') {
        const tree = jq('[data-widget="treeview"]')
        if (tree?.length && typeof tree.Treeview === 'function') {
          tree.Treeview('init')
          return
        }
      }
    } catch (e) {
      console.warn('Failed to initialize AdminLTE treeview:', e)
      return
    }
    if (attempt < 10) {
      this.scheduleTreeviewInit(attempt + 1)
    }
  }

  public setLastOpenedSubTree(item) {
    if (!item.title && this.lastOpenedSubTree) {
      try {
        // @ts-ignore
        document?.getElementById(this.lastOpenedSubTree)?.querySelector('a.nav-link')?.querySelector('i.right')?.click()
      } catch (_) {
        console.error(_)
      }
    }
    this.lastOpenedSubTree = item.title
    this.core.services.storage.set(this.core.services.storage.keys.routing.lastOpenedSubTree, this.lastOpenedSubTree)
  }

  public isActiveLink(item: MenuItems) {
    if (item.link && item.link[0]) {
      return item.link[0] === this.router.url
    } else {
      return false
    }
  }

  public isSubmenuActive(item: MenuItems): boolean {
    return item.sublinks.some(subitem => this.isActiveLink(subitem))
  }
}
